// API Configuration
// Dynamically determines the API URL based on environment and hostname

/**
 * Get the backend API URL dynamically
 *
 * Priority:
 * 1. REACT_APP_API_URL environment variable (if set)
 * 2. Dynamic detection based on window.location
 *
 * Dynamic Detection Logic:
 * - localhost:3000 → http://localhost:5001/api
 * - localhost:3003 → http://localhost:5002/api
 * - 192.168.x.x:3003 → http://192.168.x.x:5002/api (mobile testing)
 * - Production domain → https://domain.com/api
 *
 * This allows:
 * ✅ Local development on laptop
 * ✅ Mobile testing via IP address
 * ✅ Production deployment without code changes
 */

// Store the computed URL so we only calculate it once per browser session
let _computedUrl = null;

export const getApiUrl = () => {
  // Return cached value if already computed in this browser session
  if (_computedUrl !== null) {
    return _computedUrl;
  }

  // If explicitly set in .env, use that
  if (process.env.REACT_APP_API_URL) {
    _computedUrl = process.env.REACT_APP_API_URL;
    console.log('🔗 API URL (from env):', _computedUrl);
    return _computedUrl;
  }

  // Dynamic detection based on current URL (browser context)
  if (typeof window === 'undefined') {
    return 'http://localhost:5002/api'; // Fallback for SSR
  }

  const { hostname, port, protocol } = window.location;

  // Port mapping for development
  const getBackendPort = () => {
    if (port === '3000') return '5001';
    if (port === '3003') return '5002';
    return port;
  };

  // Determine backend protocol
  // Local development: always HTTP
  // Production/staging: use HTTPS
  const isLocalDevelopment = hostname === 'localhost' || hostname.startsWith('192.168.');
  const backendProtocol = isLocalDevelopment ? 'http' : 'https';

  const backendPort = getBackendPort();
  _computedUrl = `${backendProtocol}://${hostname}${backendPort ? `:${backendPort}` : ''}/api`;

  // Log in development
  if (process.env.NODE_ENV === 'development') {
    console.log('🔗 API URL (computed):', _computedUrl);
    console.log('🌍 From location:', window.location.href);
  }

  return _computedUrl;
};

// Export API_URL that calls the getter function
// NOTE: This will be evaluated when the module is first imported in the browser
// Since React dev server runs in the browser, window.location will be the browser's location
export const API_URL = getApiUrl();
